import { Button } from '../ui';
import Icon from '../ui/Icon';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownComponents } from '../SafeMarkdown';
import { AttachmentLinks } from '../SessionAttachments';
import { extractMarkdown } from '../../lib/format';

const QUESTION_STATUS_SUFFIX = {
  pending: ' · ожидает ответа',
  answered: '',
  expired: ' · истёк',
  cancelled: ' · отменён',
};
function DocumentCard({ entry, onOpenDocument }) {
  return <button type="button" className="session-document-card" aria-label={entry.title}
    onClick={() => onOpenDocument?.(entry)}>
    <span className="session-document-card-icon"><Icon name="document" size={24} /></span>
    <span className="session-document-card-title">{entry.title}</span>
  </button>;
}

/**
 * One transcript row. A pending question renders as an outcome row only — the
 * question is answered through the composer takeover, never through a second
 * interactive copy in the flow.
 */
export default function TranscriptEntry({
  entry, onExportDocx, exporting, ownedByComposer, onOpenDocument,
  exportFormats, onExportArtifact, busyExport,
  administrative = false, onOpenAttachment,
}) {
  switch (entry.kind) {
    case 'user_message':
    case 'user_answer': {
      const failed = entry.localStatus === 'failed';
      const sending = entry.localStatus === 'sending';
      return (
        <div
          className={
            'chat-bubble ' +
            (entry.kind === 'user_answer' ? 'chat-bubble-answer' : 'chat-bubble-user') +
            (sending ? ' chat-bubble-sending' : '') +
            (failed ? ' chat-bubble-failed' : '')
          }
        >
          <div className="bubble-label bubble-label-user">
            {administrative ? 'Клиент' : 'Вы'}
            {sending && <span className="bubble-state"> · отправляется</span>}
            {failed && <span className="bubble-state bubble-state-failed"> · не отправлено</span>}
          </div>
          <div className="user-message-text">{entry.text}</div>
          {administrative ? (entry.attachments || []).map(file => <Button key={file.id} onClick={() => onOpenAttachment?.(file)}>{file.filename}</Button>) : <AttachmentLinks items={entry.attachments} />}
          {failed && entry.onRetry && (
            <Button className="btn btn-secondary btn-sm mt-2" onClick={entry.onRetry}>
              Повторить
            </Button>
          )}
        </div>
      );
    }

    case 'assistant_message':
      return (
        <div className="chat-bubble chat-bubble-deliverable">
          <div className="bubble-label">Ответ агента</div>
          <div className="markdown">
            <ReactMarkdown components={safeMarkdownComponents}>{extractMarkdown(entry.text)}</ReactMarkdown>
          </div>
        </div>
      );

    case 'agent_question': {
      const pending = entry.question_status === 'pending';
      // While the composer has taken this question over it owns the body, so
      // the transcript keeps a bare outcome marker. Rendering the question in
      // both places put the same text and the same context paragraph on screen
      // twice, which is the duplication the composer takeover exists to avoid.
      if (ownedByComposer) {
        return (
          <div className="chat-question-marker">
            Запрос агентов · ожидает ответа ниже
          </div>
        );
      }
      return (
        <div className={'chat-bubble chat-bubble-question' + (pending ? ' chat-bubble-pending' : '')}>
          <div className={'bubble-label' + (pending ? ' bubble-label-urgent' : '')}>
            Запрос агентов{QUESTION_STATUS_SUFFIX[entry.question_status] ?? ''}
          </div>
          <div style={{ fontWeight: pending ? 500 : 400 }}>{entry.text}</div>
          {entry.context_summary && (
            <div className="text-muted mt-2" style={{ fontSize: '0.82rem' }}>
              {entry.context_summary}
            </div>
          )}
        </div>
      );
    }

    case 'deliverable':
      if (administrative) return <div className="chat-bubble chat-bubble-deliverable">
        <button className="document-open-button" onClick={() => onOpenDocument?.(entry)}>
          <div className="bubble-label">{entry.status === 'partial' ? 'Материал исследования · есть пробелы' : 'Документ сессии'}</div>
          <div className="report-title">{entry.title}</div>
          <div className="text-muted">Открыть полный документ →</div>
        </button>
      </div>;
      return <DocumentCard entry={entry} onOpenDocument={onOpenDocument} />;

    case 'final_report':
      if (!administrative) return <DocumentCard entry={entry} onOpenDocument={onOpenDocument} />;
      return <div className="chat-bubble chat-bubble-report">
        <button className="document-open-button" aria-label={entry.title} onClick={() => onOpenDocument?.(entry)}>
          <div className="bubble-label bubble-label-done">Итоговый отчёт{entry.report_version ? ` · версия ${entry.report_version}` : ''}</div>
          <div className="report-title">{entry.title}</div>
          {administrative && entry.changes_summary && <div className="text-muted">{entry.changes_summary}</div>}
          <div>Открыть документ →</div>
        </button>
      </div>;

    default:
      return null;
  }
}
